import noop from 'lodash/noop'
import {call, put, select, take, takeEvery} from 'redux-saga/effects'

import * as rtkActions from '../../actions/rentToKeep'
import * as rtkSelectors from '../../store/rentToKeep'
import * as userSelectors from '../../store/user'
import * as userInteractionsActions from '../../actions/userInteractions'
import {worldpayExpirationDate, worldpayPaypageRegistrationId} from '../../store/worldpay'
import {forUserAndEvent} from '../login'
import {snakeCaseObjectKeys} from '../../api/helpers'
import {
  calculateRentToKeepPurchaseTotals,
  getEligibleRTKItems,
  onStatus,
  purchaseRentToKeepItems,
} from '../../api/api'
import {isEligibleOrderItemsMissingData} from './helpers'
import {REDUX} from '../../const'
import {saveBillingInformationFromReduxForm} from '../checkout/helpers'
import {checkoutApiError, PROCESS_CHECKOUT} from '../../checkoutErrorCodes'

export function* startRentToKeepFlowWorker({payload: {eventId}}) {
  yield put(rtkActions.getEligibleRTKItems(eventId))
}

export function* startRentToKeepFlowSaga() {
  yield takeEvery(rtkActions.START_RTK_FLOW, startRentToKeepFlowWorker)
}

export function* getEligibleRTKItemsWorker({payload: {eventId}}) {
  yield put(rtkActions.getEligibleRTKItemsPending())

  try {
    yield call(forUserAndEvent)
    const userId = yield select(userSelectors.id)
    const response = yield call(getEligibleRTKItems, {eventId, userId})
    const result = onStatus({
      OK: ({json}) => json,
      DEFAULT: () => {
        throw new Error('Unable to fetch eligible RTK items.')
      },
    })(response)

    // check if there are missing item data from response
    if (isEligibleOrderItemsMissingData(result.orders)) {
      throw new Error('Missing item data.')
    }
    yield put(rtkActions.getEligibleRTKItemsSuccess(result))
  } catch (error) {
    yield put(rtkActions.getEligibleRTKItemsFailure(error))
  }
}

export function* getEligibleRTKItemsSaga() {
  yield takeEvery(rtkActions.GET_ELIGIBLE_RTK_ITEMS, getEligibleRTKItemsWorker)
}

export function* submitNewCardBillingStep() {
  yield put(rtkActions.saveRTKBillingInformationPending())

  try {
    yield call(saveBillingInformationFromReduxForm, REDUX.RTK_BILLING_FORM)
    yield put(rtkActions.saveRTKBillingInformationSuccess())
  } catch (e) {
    yield put(rtkActions.saveRTKBillingInformationFailure(e))
  }
}

export function* submitBillingStepSaga() {
  yield take(rtkActions.SUBMIT_BILLING_STEP)

  const isChargingCardOnFile = yield select(rtkSelectors.isChargingCardOnFile)

  if (isChargingCardOnFile) {
    yield put(rtkActions.submitChargeCardOnFile())
  } else {
    yield call(submitNewCardBillingStep)
  }
}

export function* calculateRTKOrderTotalsWorker({payload}) {
  yield put(rtkActions.calculateRTKOrderTotalsPending())

  try {
    const formattedPayload = {items: payload.map(snakeCaseObjectKeys)}
    const response = yield call(calculateRentToKeepPurchaseTotals, formattedPayload)
    const result = onStatus({
      OK: ({json}) => json,
      DEFAULT: () => {
        throw new Error('Unable to calculate RTK order summary totals.')
      },
    })(response)

    yield put(rtkActions.calculateRTKOrderTotalsSuccess(result))
  } catch (error) {
    yield put(rtkActions.calculateRTKOrderTotalsFailure(error))
  }
}

export function* calculateRTKOrderTotalsSaga() {
  yield takeEvery(rtkActions.CALCULATE_RTK_ORDER_TOTALS, calculateRTKOrderTotalsWorker)
}

export function* getRTKPurchasePayload() {
  const purchaseItems = yield select(rtkSelectors.itemsToPurchase)
  const paypageRegistrationId = yield select(worldpayPaypageRegistrationId)
  const cardExpiration = yield select(worldpayExpirationDate)
  const isCardOnFileCharge = yield select(rtkSelectors.isChargingCardOnFile)

  return snakeCaseObjectKeys({
    cardExpiration,
    isCardOnFileCharge,
    paypageRegistrationId,
    items: purchaseItems.map(snakeCaseObjectKeys),
  })
}

export function* completeRTKCheckoutWorker() {
  yield put(rtkActions.submitRTKPurchasePending())

  try {
    const payload = yield call(getRTKPurchasePayload)
    const response = yield call(purchaseRentToKeepItems, payload)

    onStatus({
      OK: noop,
      400: ({json}) => {
        throw json.errors[0]
      },
      DEFAULT: ({status}) => {
        throw checkoutApiError(PROCESS_CHECKOUT, status)
      },
    })(response)

    yield put(rtkActions.submitRTKPurchaseSuccess())
  } catch (error) {
    yield put(rtkActions.submitRTKPurchaseFailure(error))
  }
}

export function* completeRTKCheckoutSaga() {
  yield takeEvery(rtkActions.SUBMIT_RTK_PURCHASE, completeRTKCheckoutWorker)
}

export function* activateRtkCheckoutErrorModalWorker() {
  const shouldShowErrorModal = yield select(rtkSelectors.shouldShowErrorModal)
  if (shouldShowErrorModal) {
    yield put(userInteractionsActions.openRtkCheckoutErrorModal())
  }
}

export function* activateRtkCheckoutErrorModalSaga() {
  yield takeEvery(rtkActions.SUBMIT_RTK_PURCHASE_FAILURE, activateRtkCheckoutErrorModalWorker)
}
